import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, RefreshCw, Trash2 } from 'lucide-react';
import { apiService, MarkerColor, MarkerEntityType, MarkerInfo, MarkerRecord, SearchResult } from '../services/api';
import { useTabState } from '../contexts/TabStateContext';
import { getTabPath } from '../navigation/tabRoutes';
import { segmentCache } from '../services/segmentCache';
import { patchSearchResults, shouldPatchSearchResult } from '../utils/searchResultPatching';
import MarkerEditor from './MarkerEditor';
import CustomSelect from './CustomSelect';
import { MarkedGrid, MarkedTabControls, ViewMode, GroupBy } from './marked';
import './MarkedTab.css';

type TypeFilter = MarkerEntityType | 'all';
type ColorFilter = MarkerColor | 'all';

const MARKERS_PAGE_SIZE = 50;

const emptyMarkerInfo: MarkerInfo = {
  marker_id: null,
  color: null,
  note: null,
  updated_at: null,
};

const toMarkerInfo = (record: MarkerRecord) => ({
  marker_id: record.marker_id,
  color: record.color ?? null,
  note: record.note ?? null,
  updated_at: record.updated_at ?? null,
});

const buildOpenUrl = (record: MarkerRecord): string | null => {
  const indexParams = new URLSearchParams();

  const indexId = record.index_id || null;
  if (indexId) {
    indexParams.set('index', indexId);
  }

  const videoId = record.video_id || null;
  if (!videoId) {
    // Without a video id we cannot navigate to a concrete in-app detail view.
    return null;
  }

  indexParams.set('view', 'video');
  indexParams.set('video', videoId);

  if (record.entity_type === 'prompt_run' && record.run_id) {
    indexParams.set('run', record.run_id);
  }

  if (
    (record.entity_type === 'segment' || record.entity_type === 'metadata_field') &&
    typeof record.segment_start_time === 'number'
  ) {
    indexParams.set('t', `${record.segment_start_time}`);
  }

  if (record.entity_type === 'metadata_field') {
    if (record.run_id) indexParams.set('run', record.run_id);
    if (record.field_key) indexParams.set('field', record.field_key);
  }

  return `${getTabPath('index')}?${indexParams.toString()}`;
};

const describeMarker = (record: MarkerRecord): string => {
  if (record.entity_type === 'video') {
    return record.video_id ? `Video ${record.video_id}` : 'Video';
  }
  if (record.entity_type === 'segment') {
    return record.segment_id ? `Segment ${record.segment_id}` : 'Segment';
  }
  if (record.entity_type === 'prompt_run') {
    return record.run_id ? `Run ${record.run_id}` : 'Run';
  }
  if (record.entity_type === 'metadata_field') {
    const key = record.field_key ? `.${record.field_key}` : '';
    return record.segment_id ? `Field ${record.segment_id}${key}` : `Field${key}`;
  }
  return record.entity_type;
};

const MarkedTab: React.FC = () => {
  const navigate = useNavigate();
  const { setSearchTabState } = useTabState();
  const [records, setRecords] = useState<MarkerRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [colorFilter, setColorFilter] = useState<ColorFilter>('all');
  const [indexFilter, setIndexFilter] = useState<string>('all');

  // View mode state
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [tilesPerRow, setTilesPerRow] = useState(4);
  const [showDetails, setShowDetails] = useState(false);
  const [groupBy, setGroupBy] = useState<GroupBy>('none');

  const uniqueIndexes = useMemo(() => {
    const ids = new Set<string>();
    records.forEach((r) => {
      if (r.index_id) ids.add(r.index_id);
    });
    return Array.from(ids.values()).sort();
  }, [records]);

  const filteredRecords = useMemo(() => {
    return records.filter((r) => {
      if (typeFilter !== 'all' && r.entity_type !== typeFilter) return false;
      if (colorFilter !== 'all' && r.color !== colorFilter) return false;
      if (indexFilter !== 'all' && (r.index_id || '') !== indexFilter) return false;
      return true;
    });
  }, [records, typeFilter, colorFilter, indexFilter]);

  const loadPage = async (cursor?: string | null, append = false) => {
    setError(null);
    if (!append) setLoading(true);
    if (append) setLoadingMore(true);
    try {
      const response = await apiService.listMarkers(MARKERS_PAGE_SIZE, cursor || undefined);
      setRecords((prev) => (append ? [...prev, ...response.data] : response.data));
      setHasMore(Boolean(response.pagination?.has_more));
      setNextCursor(response.pagination?.next_cursor || null);
    } catch (err) {
      console.error('Failed to load markers:', err);
      setError('Failed to load markers. Please retry.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    loadPage(null, false);
  }, []);

  const applyMarkerSideEffects = (previous: MarkerRecord, next: MarkerRecord | null) => {
    const info: MarkerInfo = next ? toMarkerInfo(next) : emptyMarkerInfo;
    const stable = next || previous;

    if (stable.entity_type === 'video' && stable.video_id) {
      return;
    }

    if (stable.entity_type === 'segment' && stable.segment_id && stable.video_id) {
      const segmentId = stable.segment_id;
      const patch: Partial<SearchResult> = { marker: info };

      setSearchTabState((prev) => {
        const patchResults = (results: SearchResult[]): SearchResult[] => patchSearchResults(results, segmentId, patch);
        const patchAgenticMessages = (messages: any[]): any[] =>
          messages.map((m) =>
            Array.isArray(m.segments) ? { ...m, segments: patchResults(m.segments as SearchResult[]) } : m
          );

        const nextState: any = {
          ...prev,
          vectorSearch: { ...prev.vectorSearch, searchResults: patchResults(prev.vectorSearch.searchResults) },
          conditionSearch: { ...prev.conditionSearch, searchResults: patchResults(prev.conditionSearch.searchResults) },
          agenticSearch: {
            ...prev.agenticSearch,
            suggestedSegments: patchResults(prev.agenticSearch.suggestedSegments),
            messages: patchAgenticMessages(prev.agenticSearch.messages),
          },
        };

        if (shouldPatchSearchResult(prev.selectedSearchResult, segmentId)) {
          nextState.selectedSearchResult = { ...prev.selectedSearchResult, ...patch };
        }

        return nextState;
      });

      segmentCache.patchCachedSegment(stable.video_id, stable.run_id || null, segmentId, { marker: info }).catch((err) => {
        console.warn('Failed to patch cached segment marker:', err);
      });

      return;
    }

    if (stable.entity_type === 'prompt_run' && stable.video_id && stable.run_id) {
      segmentCache.patchCachedPromptRun(stable.video_id, stable.run_id, { marker: info }).catch((err) => {
        console.warn('Failed to patch cached prompt run marker:', err);
      });

      return;
    }

    if (
      stable.entity_type === 'metadata_field' &&
      stable.video_id &&
      stable.segment_id &&
      stable.field_key
    ) {
      const videoId = stable.video_id;
      const segmentId = stable.segment_id;
      const fieldKey = stable.field_key;
      const runScopeId = stable.run_id || null;
      const shouldDelete = !info.marker_id && !info.color && !(info.note || '').trim();

      setSearchTabState((prev) => {
        const patchFieldMarkers = (results: SearchResult[]): SearchResult[] =>
          results.map((result) => {
            if (!shouldPatchSearchResult(result, segmentId, runScopeId)) return result;
            const nextMap = { ...(result.extracted_metadata_markers || {}) };
            if (shouldDelete) {
              delete nextMap[fieldKey];
            } else {
              nextMap[fieldKey] = info;
            }
            return { ...result, extracted_metadata_markers: nextMap };
          });

        const patchAgenticMessages = (messages: any[]): any[] =>
          messages.map((m) =>
            Array.isArray(m.segments) ? { ...m, segments: patchFieldMarkers(m.segments as SearchResult[]) } : m
          );

        const nextState: any = {
          ...prev,
          vectorSearch: { ...prev.vectorSearch, searchResults: patchFieldMarkers(prev.vectorSearch.searchResults) },
          conditionSearch: { ...prev.conditionSearch, searchResults: patchFieldMarkers(prev.conditionSearch.searchResults) },
          agenticSearch: {
            ...prev.agenticSearch,
            suggestedSegments: patchFieldMarkers(prev.agenticSearch.suggestedSegments),
            messages: patchAgenticMessages(prev.agenticSearch.messages),
          },
        };

        if (shouldPatchSearchResult(prev.selectedSearchResult, segmentId, runScopeId)) {
          const nextMap = { ...(prev.selectedSearchResult?.extracted_metadata_markers || {}) };
          if (shouldDelete) {
            delete nextMap[fieldKey];
          } else {
            nextMap[fieldKey] = info;
          }
          nextState.selectedSearchResult = { ...prev.selectedSearchResult, extracted_metadata_markers: nextMap };
        }

        return nextState;
      });

      segmentCache
        .getCachedSegments(videoId, runScopeId)
        .then((cached) => {
          if (!cached) return;
          const nextSegments = cached.map((seg: any) => {
            if (seg.segment_id !== segmentId) return seg;
            const nextMap = { ...(seg.metadata_markers || {}) };
            if (shouldDelete) {
              delete nextMap[fieldKey];
            } else {
              nextMap[fieldKey] = info;
            }
            return { ...seg, metadata_markers: nextMap };
          });
          return segmentCache.cacheSegments(videoId, runScopeId, nextSegments);
        })
        .catch((err) => console.warn('Failed to patch cached metadata field marker:', err));
    }
  };

  const handleRemove = async (markerId: string) => {
    const removed = records.find((r) => r.marker_id === markerId) || null;
    try {
      await apiService.deleteMarker(markerId);
      setRecords((prev) => prev.filter((r) => r.marker_id !== markerId));
      if (removed) {
        applyMarkerSideEffects(removed, null);
      }
    } catch (err) {
      console.error('Failed to delete marker:', err);
      setError('Failed to delete marker. Please retry.');
    }
  };

  return (
    <div className="marked-tab">
      <div className="marked-header">
        <div>
          <h2>Marked</h2>
          <p className="marked-subtitle">Your color tags and notes across videos, segments, fields, and runs.</p>
        </div>
        <div className="marked-header-actions">
          <button
            type="button"
            className="marked-refresh"
            onClick={() => loadPage(null, false)}
            disabled={loading}
            title="Refresh"
          >
            <RefreshCw size={16} className={loading ? 'spinning' : ''} />
            Refresh
          </button>
        </div>
      </div>

      <div className="marked-filters">
        <div className="marked-filter">
          <span>Type</span>
          <CustomSelect
            value={typeFilter}
            onChange={(value) => setTypeFilter(value as TypeFilter)}
            options={[
              { value: 'all', label: 'All Types' },
              { value: 'video', label: 'Videos' },
              { value: 'segment', label: 'Segments' },
              { value: 'metadata_field', label: 'Fields' },
              { value: 'prompt_run', label: 'Runs' },
            ]}
          />
        </div>

        <div className="marked-filter">
          <span>Color</span>
          <CustomSelect
            value={colorFilter}
            onChange={(value) => setColorFilter(value as ColorFilter)}
            options={[
              { value: 'all', label: 'All Colors' },
              { value: 'red', label: 'Red' },
              { value: 'orange', label: 'Orange' },
              { value: 'yellow', label: 'Yellow' },
              { value: 'green', label: 'Green' },
              { value: 'blue', label: 'Blue' },
              { value: 'gray', label: 'Gray' },
            ]}
          />
        </div>

        <div className="marked-filter">
          <span>Index</span>
          <CustomSelect
            value={indexFilter}
            onChange={setIndexFilter}
            options={[
              { value: 'all', label: 'All Indexes' },
              ...uniqueIndexes.map((id) => ({ value: id, label: id })),
            ]}
          />
        </div>
      </div>

      {error && <div className="marked-error">{error}</div>}

      {/* View Controls */}
      <MarkedTabControls
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        tilesPerRow={tilesPerRow}
        onTilesPerRowChange={setTilesPerRow}
        showDetails={showDetails}
        onShowDetailsChange={setShowDetails}
        groupBy={groupBy}
        onGroupByChange={setGroupBy}
      />

      {/* Loading State */}
      {loading && records.length === 0 && <div className="marked-empty">Loading markers...</div>}

      {/* Empty State */}
      {!loading && filteredRecords.length === 0 && <div className="marked-empty">No markers yet.</div>}

      {/* Grid View */}
      {viewMode === 'grid' && filteredRecords.length > 0 && (
        <MarkedGrid
          records={filteredRecords}
          tilesPerRow={tilesPerRow}
          showDetails={showDetails}
          groupBy={groupBy}
          onRecordClick={(record) => {
            const openUrl = buildOpenUrl(record);
            if (openUrl) {
              navigate(openUrl);
            }
          }}
          onRecordRemove={(record) => handleRemove(record.marker_id)}
          onRecordChange={(previous, next) => {
            setRecords((prev) => {
              if (!next) return prev.filter((r) => r.marker_id !== previous.marker_id);
              return prev.map((r) => (r.marker_id === previous.marker_id ? next : r));
            });
            applyMarkerSideEffects(previous, next);
          }}
        />
      )}

      {/* List View */}
      {viewMode === 'list' && filteredRecords.length > 0 && (
        <div className="marked-list">
          {filteredRecords.map((record) => {
            const openUrl = buildOpenUrl(record);
            const notePreview = (record.note || '').trim();
            const subtitleParts: string[] = [];
            if (record.index_id) subtitleParts.push(`index: ${record.index_id}`);
            if (record.video_id) subtitleParts.push(`video: ${record.video_id}`);
            if (record.segment_id) subtitleParts.push(`segment: ${record.segment_id}`);
            if (record.run_id) subtitleParts.push(`run: ${record.run_id}`);
            if (record.field_key) subtitleParts.push(`field: ${record.field_key}`);

            return (
              <div key={record.marker_id} className="marked-item">
                <div className="marked-item-left">
                  <MarkerEditor
                    marker={toMarkerInfo(record)}
                    target={{
                      entity_type: record.entity_type,
                      index_id: record.index_id || undefined,
                      run_id: record.run_id || undefined,
                      video_id: record.video_id || undefined,
                      segment_id: record.segment_id || undefined,
                      field_key: record.field_key || undefined,
                      segment_start_time: record.segment_start_time ?? undefined,
                      segment_end_time: record.segment_end_time ?? undefined,
                    }}
                    onRecordChange={(next) => {
                      setRecords((prev) => {
                        if (!next) return prev.filter((r) => r.marker_id !== record.marker_id);
                        return prev.map((r) => (r.marker_id === record.marker_id ? next : r));
                      });
                      applyMarkerSideEffects(record, next);
                    }}
                  />

                  <div className="marked-item-text">
                    <div className="marked-item-title">{describeMarker(record)}</div>
                    {subtitleParts.length > 0 && <div className="marked-item-subtitle">{subtitleParts.join('  •  ')}</div>}
                    {notePreview && <div className="marked-item-note">{notePreview}</div>}
                    {!notePreview && <div className="marked-item-note muted">No note</div>}
                  </div>
                </div>

                <div className="marked-item-actions">
                  <button
                    type="button"
                    className="marked-action"
                    onClick={() => {
                      if (!openUrl) return;
                      navigate(openUrl);
                    }}
                    disabled={!openUrl}
                    title={openUrl ? 'Open' : 'Open is unavailable for this marker'}
                  >
                    <ExternalLink size={15} />
                    Open
                  </button>
                  <button
                    type="button"
                    className="marked-action danger"
                    onClick={() => handleRemove(record.marker_id)}
                    title="Remove"
                  >
                    <Trash2 size={15} />
                    Remove
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {hasMore && (
        <div className="marked-footer">
          <button
            type="button"
            className="marked-load-more"
            onClick={() => loadPage(nextCursor, true)}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading...' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
};

export default MarkedTab;
